import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { EASE } from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AREAS = [
  "Executive Coaching",
  "Business Development",
  "Speaking Engagement",
  "Consulting",
  "General Inquiry",
];

export default function InquiryForm({ presetArea, dark = false, testidPrefix = "inquiry", submitLabel = "Send Inquiry" }) {
  const [area, setArea] = useState(presetArea || AREAS[0]);
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [status, setStatus] = useState("idle");

  const inputCls = dark
    ? "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-ivory placeholder:text-white/25 transition-colors duration-300"
    : "w-full bg-transparent border-b border-obsidian/15 focus:border-gold outline-none py-3 text-obsidian placeholder:text-obsidian/30 transition-colors duration-300";
  const labelCls = `font-mono text-[10px] tracking-[0.25em] uppercase ${dark ? "text-white/40" : "text-obsidian/45"}`;

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, { ...form, area_of_interest: area });
      setStatus("sent");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <motion.div
        data-testid={`${testidPrefix}-success`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className={`border ${dark ? "border-gold/40" : "border-gold/50"} p-10 text-center`}
      >
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold text-gold mb-6">
          <Check size={20} />
        </span>
        <p className={`font-serif text-2xl mb-3 ${dark ? "text-ivory" : "text-obsidian"}`}>
          Thank you, {form.name.split(" ")[0]}.
        </p>
        <p className={`text-sm font-light ${dark ? "text-white/50" : "text-stone-text"}`}>
          Your inquiry has been received. Sharon's team will respond within 1–2 business days.
        </p>
        <button
          data-testid={`${testidPrefix}-send-another-button`}
          onClick={() => { setStatus("idle"); setForm({ name: "", email: "", organization: "", message: "" }); }}
          className="mt-8 font-mono text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold-dark transition-colors"
        >
          Send Another Inquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="space-y-8">
      <div>
        <p className={`${labelCls} mb-4`}>Area of Interest</p>
        <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-area-options`}>
          {AREAS.map((a) => (
            <button
              key={a}
              type="button"
              data-testid={`${testidPrefix}-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setArea(a)}
              className={`px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
                area === a
                  ? "bg-gold text-obsidian border-gold"
                  : dark
                    ? "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
                    : "border-obsidian/15 text-obsidian/60 hover:border-gold hover:text-gold-dark"
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-name`}>Full Name *</label>
          <input
            id={`${testidPrefix}-name`}
            data-testid={`${testidPrefix}-name-input`}
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-email`}>Email *</label>
          <input
            id={`${testidPrefix}-email`}
            data-testid={`${testidPrefix}-email-input`}
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@company.com"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor={`${testidPrefix}-organization`}>Organization / Business</label>
        <input
          id={`${testidPrefix}-organization`}
          data-testid={`${testidPrefix}-organization-input`}
          value={form.organization}
          onChange={(e) => setForm({ ...form, organization: e.target.value })}
          placeholder="Company or organization (optional)"
          className={inputCls}
        />
      </div>

      <div>
        <label className={labelCls} htmlFor={`${testidPrefix}-message`}>Brief Message *</label>
        <textarea
          id={`${testidPrefix}-message`}
          data-testid={`${testidPrefix}-message-input`}
          required
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Tell us where you are, and where you'd like to go."
          className={`${inputCls} resize-none`}
        />
      </div>

      <button
        type="submit"
        data-testid={`${testidPrefix}-submit-button`}
        disabled={status === "sending"}
        className="bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300 disabled:opacity-50"
      >
        {status === "sending" ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}
